import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Deal {
  id?: string;
  deal_title: string;
  seller_name: string;
  seller_email?: string;
  seller_phone?: string;
  seller_address?: string;
  buyer_name: string;
  buyer_address?: string;
  buyer_current_on_contract: boolean;
  buyer_payment_history?: string;
  date_of_sale?: string;
  first_payment_date?: string;
  sales_price?: number;
  down_payment_amount?: number;
  beginning_balance?: number;
  interest_rate?: number;
  payment_amount?: number;
  original_term_months?: number;
  has_balloon_payment: boolean;
  balloon_amount?: number;
  balloon_due_date?: string;
  property_type?: string;
  property_description?: string;
  property_address?: string;
  estimated_property_value?: number;
  current_balance?: number;
  payments_remaining?: number;
  status: string;
  bank_notes?: string;
  internal_notes?: string;
  offer_price?: number;
  offer_date?: string;
}

interface DealEntryFormProps {
  deal?: Deal | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DealEntryForm = ({ deal, onClose, onSuccess }: DealEntryFormProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState<Deal>({
    deal_title: deal?.deal_title || '',
    seller_name: deal?.seller_name || '',
    seller_email: deal?.seller_email || '',
    seller_phone: deal?.seller_phone || '',
    seller_address: deal?.seller_address || '',
    buyer_name: deal?.buyer_name || '',
    buyer_address: deal?.buyer_address || '',
    buyer_current_on_contract: deal?.buyer_current_on_contract ?? true,
    buyer_payment_history: deal?.buyer_payment_history || '',
    date_of_sale: deal?.date_of_sale || '',
    first_payment_date: deal?.first_payment_date || '',
    sales_price: deal?.sales_price || undefined,
    down_payment_amount: deal?.down_payment_amount || undefined,
    beginning_balance: deal?.beginning_balance || undefined,
    interest_rate: deal?.interest_rate || undefined,
    payment_amount: deal?.payment_amount || undefined,
    original_term_months: deal?.original_term_months || undefined,
    has_balloon_payment: deal?.has_balloon_payment || false,
    balloon_amount: deal?.balloon_amount || undefined,
    balloon_due_date: deal?.balloon_due_date || '',
    property_type: deal?.property_type || '',
    property_description: deal?.property_description || '',
    property_address: deal?.property_address || '',
    estimated_property_value: deal?.estimated_property_value || undefined,
    current_balance: deal?.current_balance || undefined,
    payments_remaining: deal?.payments_remaining || undefined,
    status: deal?.status || 'draft',
    bank_notes: deal?.bank_notes || '',
    internal_notes: deal?.internal_notes || '',
    offer_price: deal?.offer_price || undefined,
    offer_date: deal?.offer_date || ''
  });

  const handleInputChange = (field: keyof Deal, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const dealData = {
        ...formData,
        sales_price: formData.sales_price || null,
        down_payment_amount: formData.down_payment_amount || null,
        beginning_balance: formData.beginning_balance || null,
        interest_rate: formData.interest_rate || null,
        payment_amount: formData.payment_amount || null,
        original_term_months: formData.original_term_months || null,
        balloon_amount: formData.balloon_amount || null,
        estimated_property_value: formData.estimated_property_value || null,
        current_balance: formData.current_balance || null,
        payments_remaining: formData.payments_remaining || null,
        offer_price: formData.offer_price || null,
        date_of_sale: formData.date_of_sale || null,
        first_payment_date: formData.first_payment_date || null,
        balloon_due_date: formData.balloon_due_date || null,
        offer_date: formData.offer_date || null,
        seller_email: formData.seller_email || null,
        seller_phone: formData.seller_phone || null,
        seller_address: formData.seller_address || null,
        buyer_address: formData.buyer_address || null,
        buyer_payment_history: formData.buyer_payment_history || null,
        property_type: formData.property_type || null,
        property_description: formData.property_description || null,
        property_address: formData.property_address || null,
        bank_notes: formData.bank_notes || null,
        internal_notes: formData.internal_notes || null
      };

      if (deal?.id) {
        const { error } = await supabase
          .from('deals')
          .update(dealData)
          .eq('id', deal.id);

        if (error) throw error;
        toast({ title: "Deal updated successfully" });
      } else {
        const { error } = await supabase
          .from('deals')
          .insert([dealData]);

        if (error) throw error;
        toast({ title: "Deal created successfully" });
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving deal:', error);
      toast({
        title: "Error saving deal",
        description: "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onClose} className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Deals
        </Button>
        <h1 className="text-2xl font-bold">
          {deal ? 'Edit Deal' : 'New Deal Entry'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Deal Title */}
        <Card>
          <CardHeader>
            <CardTitle>Deal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="deal_title">Deal Title *</Label>
              <Input
                id="deal_title"
                value={formData.deal_title}
                onChange={(e) => handleInputChange('deal_title', e.target.value)}
                placeholder="Enter deal title"
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Seller Information */}
        <Card>
          <CardHeader>
            <CardTitle>Seller Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="seller_name">Seller Name *</Label>
              <Input
                id="seller_name"
                value={formData.seller_name}
                onChange={(e) => handleInputChange('seller_name', e.target.value)}
                placeholder="Enter seller name"
                required
              />
            </div>
            <div>
              <Label htmlFor="seller_email">Email</Label>
              <Input
                id="seller_email"
                type="email"
                value={formData.seller_email}
                onChange={(e) => handleInputChange('seller_email', e.target.value)}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label htmlFor="seller_phone">Phone</Label>
              <Input
                id="seller_phone"
                value={formData.seller_phone}
                onChange={(e) => handleInputChange('seller_phone', e.target.value)}
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <Label htmlFor="seller_address">Address</Label>
              <Input
                id="seller_address"
                value={formData.seller_address}
                onChange={(e) => handleInputChange('seller_address', e.target.value)}
                placeholder="Enter seller address"
              />
            </div>
          </CardContent>
        </Card>

        {/* Buyer Information */}
        <Card>
          <CardHeader>
            <CardTitle>Buyer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="buyer_name">Buyer Name *</Label>
                <Input
                  id="buyer_name"
                  value={formData.buyer_name}
                  onChange={(e) => handleInputChange('buyer_name', e.target.value)}
                  placeholder="Enter buyer name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="buyer_address">Buyer Address</Label>
                <Input
                  id="buyer_address"
                  value={formData.buyer_address}
                  onChange={(e) => handleInputChange('buyer_address', e.target.value)}
                  placeholder="Enter buyer address"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="buyer_current_on_contract"
                checked={formData.buyer_current_on_contract}
                onCheckedChange={(checked) => handleInputChange('buyer_current_on_contract', checked)}
              />
              <Label htmlFor="buyer_current_on_contract">Buyer is current on contract</Label>
            </div>
            <div>
              <Label htmlFor="buyer_payment_history">Payment History Summary</Label>
              <Textarea
                id="buyer_payment_history"
                value={formData.buyer_payment_history}
                onChange={(e) => handleInputChange('buyer_payment_history', e.target.value)}
                placeholder="Describe the buyer's payment history"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Original Contract Terms */}
        <Card>
          <CardHeader>
            <CardTitle>Original Contract Terms</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="date_of_sale">Date of Sale</Label>
              <Input
                id="date_of_sale"
                type="date"
                value={formData.date_of_sale}
                onChange={(e) => handleInputChange('date_of_sale', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="first_payment_date">First Payment Date</Label>
              <Input
                id="first_payment_date"
                type="date"
                value={formData.first_payment_date}
                onChange={(e) => handleInputChange('first_payment_date', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="sales_price">Sales Price</Label>
              <Input
                id="sales_price"
                type="number"
                step="0.01"
                value={formData.sales_price || ''}
                onChange={(e) => handleInputChange('sales_price', parseFloat(e.target.value) || undefined)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="down_payment_amount">Down Payment Amount</Label>
              <Input
                id="down_payment_amount"
                type="number"
                step="0.01"
                value={formData.down_payment_amount || ''}
                onChange={(e) => handleInputChange('down_payment_amount', parseFloat(e.target.value) || undefined)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="beginning_balance">Beginning Balance</Label>
              <Input
                id="beginning_balance"
                type="number"
                step="0.01"
                value={formData.beginning_balance || ''}
                onChange={(e) => handleInputChange('beginning_balance', parseFloat(e.target.value) || undefined)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="interest_rate">Interest Rate (%)</Label>
              <Input
                id="interest_rate"
                type="number"
                step="0.01"
                value={formData.interest_rate || ''}
                onChange={(e) => handleInputChange('interest_rate', parseFloat(e.target.value) || undefined)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="payment_amount">Payment Amount</Label>
              <Input
                id="payment_amount"
                type="number"
                step="0.01"
                value={formData.payment_amount || ''}
                onChange={(e) => handleInputChange('payment_amount', parseFloat(e.target.value) || undefined)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="original_term_months">Original Term (Months)</Label>
              <Input
                id="original_term_months"
                type="number"
                value={formData.original_term_months || ''}
                onChange={(e) => handleInputChange('original_term_months', parseInt(e.target.value) || undefined)}
                placeholder="360"
              />
            </div>
          </CardContent>
        </Card>

        {/* Balloon Payment */}
        <Card>
          <CardHeader>
            <CardTitle>Balloon Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="has_balloon_payment"
                checked={formData.has_balloon_payment}
                onCheckedChange={(checked) => handleInputChange('has_balloon_payment', checked)}
              />
              <Label htmlFor="has_balloon_payment">Has balloon payment</Label>
            </div>
            {formData.has_balloon_payment && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="balloon_amount">Balloon Amount</Label>
                  <Input
                    id="balloon_amount"
                    type="number"
                    step="0.01"
                    value={formData.balloon_amount || ''}
                    onChange={(e) => handleInputChange('balloon_amount', parseFloat(e.target.value) || undefined)}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="balloon_due_date">Balloon Due Date</Label>
                  <Input
                    id="balloon_due_date"
                    type="date"
                    value={formData.balloon_due_date}
                    onChange={(e) => handleInputChange('balloon_due_date', e.target.value)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Property Information */}
        <Card>
          <CardHeader>
            <CardTitle>Property Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="property_type">Property Type</Label>
                <Select value={formData.property_type} onValueChange={(value) => handleInputChange('property_type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SFOO">Single Family Owner Occupied</SelectItem>
                    <SelectItem value="NOO">Non-Owner Occupied</SelectItem>
                    <SelectItem value="Rental">Rental Property</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Vacant Land">Vacant Land</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="estimated_property_value">Estimated Property Value</Label>
                <Input
                  id="estimated_property_value"
                  type="number"
                  step="0.01"
                  value={formData.estimated_property_value || ''}
                  onChange={(e) => handleInputChange('estimated_property_value', parseFloat(e.target.value) || undefined)}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="property_address">Property Address</Label>
              <Input
                id="property_address"
                value={formData.property_address}
                onChange={(e) => handleInputChange('property_address', e.target.value)}
                placeholder="Enter property address"
              />
            </div>
            <div>
              <Label htmlFor="property_description">Property Description</Label>
              <Textarea
                id="property_description"
                value={formData.property_description}
                onChange={(e) => handleInputChange('property_description', e.target.value)}
                placeholder="Brief description of the property"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Current Status */}
        <Card>
          <CardHeader>
            <CardTitle>Current Contract Status</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="current_balance">Current Balance</Label>
              <Input
                id="current_balance"
                type="number"
                step="0.01"
                value={formData.current_balance || ''}
                onChange={(e) => handleInputChange('current_balance', parseFloat(e.target.value) || undefined)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="payments_remaining">Payments Remaining</Label>
              <Input
                id="payments_remaining"
                type="number"
                value={formData.payments_remaining || ''}
                onChange={(e) => handleInputChange('payments_remaining', parseInt(e.target.value) || undefined)}
                placeholder="Number of payments"
              />
            </div>
          </CardContent>
        </Card>

        {/* Offer Information */}
        <Card>
          <CardHeader>
            <CardTitle>Your Offer</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="offer_price">Offer Price</Label>
              <Input
                id="offer_price"
                type="number"
                step="0.01"
                value={formData.offer_price || ''}
                onChange={(e) => handleInputChange('offer_price', parseFloat(e.target.value) || undefined)}
                placeholder="Enter your offer amount"
              />
            </div>
            <div>
              <Label htmlFor="offer_date">Offer Date</Label>
              <Input
                id="offer_date"
                type="date"
                value={formData.offer_date}
                onChange={(e) => handleInputChange('offer_date', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="internal_notes">Internal Notes</Label>
              <Textarea
                id="internal_notes"
                value={formData.internal_notes}
                onChange={(e) => handleInputChange('internal_notes', e.target.value)}
                placeholder="Internal notes (not shared with banks)"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="bank_notes">Bank Notes</Label>
              <Textarea
                id="bank_notes"
                value={formData.bank_notes}
                onChange={(e) => handleInputChange('bank_notes', e.target.value)}
                placeholder="Additional notes for banks"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : (deal ? 'Update Deal' : 'Create Deal')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DealEntryForm;